import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Reveal, Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";

function CountUp({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  const target = parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;

  useEffect(() => {
    if (!inView) return;
    let raf;
    const dur = 1600;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setDisplay(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  const rounded = Number.isInteger(target) ? Math.round(display) : display.toFixed(1);
  return <span ref={ref}>{rounded}{suffix}</span>;
}

function Header({ eyebrow, heading }) {
  return (
    <Reveal>
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow>{eyebrow || "By The Numbers"}</Eyebrow>
          <h2 className="mt-5 max-w-xl font-serif text-3xl leading-tight text-warmwhite lg:text-4xl">{heading || "Measured, discreet, deliberate."}</h2>
        </div>
      </div>
    </Reveal>
  );
}

export default function Stats({ stats = [], comingSoon = false, eyebrow, heading }) {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/metrics").then(({ data }) => { if (live) setMetrics(data); }).catch(() => {});
    return () => { live = false; };
  }, []);

  if (comingSoon) {
    return (
      <section data-testid="stats" className="relative overflow-hidden border-y border-gold/10 bg-obsidian px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <Header eyebrow={eyebrow} heading={heading} />
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-col items-center justify-center border border-dashed border-gold/25 bg-gold/[0.03] px-8 py-16 text-center" data-testid="stats-coming-soon">
              <div className="font-serif text-4xl text-gradient-gold lg:text-5xl">Coming Soon</div>
              <p className="mt-4 max-w-md font-sans text-sm text-softgrey">Our verified figures are being finalised and will be published here shortly.</p>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  if (!stats.length) return null;

  const liveValue = (s) => {
    if (s.source && s.source !== "manual" && metrics && metrics[s.source] != null) return metrics[s.source];
    return s.value;
  };

  return (
    <section data-testid="stats" className="relative overflow-hidden border-y border-gold/10 bg-obsidian px-6 py-20 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Header eyebrow={eyebrow} heading={heading} />
        <div className="mt-14 grid gap-px overflow-hidden border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="h-full bg-obsidian p-8 text-center lg:text-left" data-testid={`stat-${i}`}>
                <div className="font-serif text-5xl text-gradient-gold lg:text-6xl">
                  <CountUp value={liveValue(s)} suffix={s.suffix || ""} />
                </div>
                <div className="mt-3 font-sans text-[11px] tracking-luxe uppercase text-softgrey">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
